import React from 'react';
import { useOrg } from '../context/OrgContext';

const PERMISSIONS = {
  USERS_READ: 'users:read',
  USERS_WRITE: 'users:write',
  ASSETS_READ: 'assets:read',
  ASSETS_WRITE: 'assets:write',
  REPORTS_READ: 'reports:read',
  REPORTS_WRITE: 'reports:write',
  COMPLIANCE_READ: 'compliance:read',
  COMPLIANCE_WRITE: 'compliance:write',
  FINANCE_READ: 'finance:read',
  FINANCE_WRITE: 'finance:write',
  ADMIN: 'admin'
};

const ROLE_PERMISSIONS = {
  org_admin: [
    PERMISSIONS.ADMIN,
    PERMISSIONS.USERS_READ,
    PERMISSIONS.USERS_WRITE,
    PERMISSIONS.ASSETS_READ,
    PERMISSIONS.ASSETS_WRITE,
    PERMISSIONS.REPORTS_READ,
    PERMISSIONS.REPORTS_WRITE,
    PERMISSIONS.COMPLIANCE_READ,
    PERMISSIONS.COMPLIANCE_WRITE,
    PERMISSIONS.FINANCE_READ,
    PERMISSIONS.FINANCE_WRITE
  ],
  admin: [
    PERMISSIONS.ADMIN,
    PERMISSIONS.USERS_READ,
    PERMISSIONS.USERS_WRITE,
    PERMISSIONS.ASSETS_READ,
    PERMISSIONS.ASSETS_WRITE,
    PERMISSIONS.REPORTS_READ,
    PERMISSIONS.REPORTS_WRITE,
    PERMISSIONS.COMPLIANCE_READ,
    PERMISSIONS.COMPLIANCE_WRITE,
    PERMISSIONS.FINANCE_READ,
    PERMISSIONS.FINANCE_WRITE
  ],
  compliance: [
    PERMISSIONS.ASSETS_READ,
    PERMISSIONS.REPORTS_READ,
    PERMISSIONS.REPORTS_WRITE,
    PERMISSIONS.COMPLIANCE_READ,
    PERMISSIONS.COMPLIANCE_WRITE
  ],
  operations: [
    PERMISSIONS.ASSETS_READ,
    PERMISSIONS.ASSETS_WRITE,
    PERMISSIONS.REPORTS_READ
  ],
  finance: [
    PERMISSIONS.REPORTS_READ,
    PERMISSIONS.FINANCE_READ,
    PERMISSIONS.FINANCE_WRITE
  ],
  read_only: [
    PERMISSIONS.ASSETS_READ,
    PERMISSIONS.REPORTS_READ
  ]
};

export function hasPermission(role, permission) {
  const permissions = ROLE_PERMISSIONS[role] || [];
  return permissions.includes(PERMISSIONS.ADMIN) || permissions.includes(permission);
}

export function hasAnyPermission(role, permissionList) {
  return permissionList.some(perm => hasPermission(role, perm));
}

export function PermissionGuard({ permission, permissions, children, fallback = null }) {
  const { orgRole } = useOrg();
  
  if (!orgRole) {
    return fallback;
  }
  
  if (permission && hasPermission(orgRole, permission)) {
    return children;
  }
  
  if (permissions && hasAnyPermission(orgRole, permissions)) {
    return children;
  }
  
  if (!permission && !permissions) {
    return children;
  }
  
  return fallback;
}

export function usePermission(permission) {
  const { orgRole } = useOrg();
  return hasPermission(orgRole, permission);
}

export function usePermissions() {
  const { orgRole } = useOrg();
  
  return {
    canReadUsers: hasPermission(orgRole, PERMISSIONS.USERS_READ),
    canWriteUsers: hasPermission(orgRole, PERMISSIONS.USERS_WRITE),
    canReadAssets: hasPermission(orgRole, PERMISSIONS.ASSETS_READ),
    canWriteAssets: hasPermission(orgRole, PERMISSIONS.ASSETS_WRITE),
    canReadReports: hasPermission(orgRole, PERMISSIONS.REPORTS_READ),
    canWriteReports: hasPermission(orgRole, PERMISSIONS.REPORTS_WRITE),
    canReadCompliance: hasPermission(orgRole, PERMISSIONS.COMPLIANCE_READ),
    canWriteCompliance: hasPermission(orgRole, PERMISSIONS.COMPLIANCE_WRITE),
    canReadFinance: hasPermission(orgRole, PERMISSIONS.FINANCE_READ),
    canWriteFinance: hasPermission(orgRole, PERMISSIONS.FINANCE_WRITE),
    isAdmin: hasPermission(orgRole, PERMISSIONS.ADMIN)
  };
}

export { PERMISSIONS };
